import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

export type FocusStateType = ReturnType<typeof useFocusState>;

export default function useFocusState(
  length: number,
  enabled = true,
  initialValue = 0,
  minValue = 0,
) {
  const store = useLocalObservable(
    () => ({
      _min: minValue,
      _length: length,
      _active: enabled,
      set active(value: boolean) {
        this._active = value;
      },
      _focused: Math.min(length - 1, Math.max(minValue, initialValue)),
      get focused(): number | undefined {
        if (!this._active) return undefined;
        return this._focused;
      },
      set focused(value: number | undefined) {
        if (value === undefined) return;
        this._focused = Math.min(this._length - 1, Math.max(this._min, value));
      },
      setForce(value: number) {
        this._focused = value;
      },
      increment(value = 1) {
        if (this._focused + value >= this._length - 1)
          this._focused = this._length - 1;
        else this._focused += value;
      },
      decrement(value = 1) {
        if (this._focused - value <= this._min) this._focused = this._min;
        else this._focused -= value;
      },
      set length(value: number) {
        this._length = value;
        this.focused = this._focused;
      },
      set min(value: number) {
        this._min = value;
        this.focused = this._focused;
      },
    }),
    { _length: false, _min: false },
  );
  useEffect(() => {
    store.active = enabled;
  }, [enabled]);
  useEffect(() => {
    store.length = length;
  }, [length]);
  useEffect(() => {
    store.min = minValue;
  }, [minValue]);
  return store;
}
